import { FeatureFlags } from '@fullstory/client-api';
import { AuditMetadata, Visibility } from '@fullstory/proto/fs/gcloud/dstore/protoauditstore';
import {
  Spec,
  SpecConfig,
  SpecObject,
  SpecObjectConfig,
  SpecObjectMapping,
  SpecObjectMappingRecord,
  SpecObjectMappingRecordMetadata,
  SpecObjectProperty,
  SpecPropertyMapping,
  SpecTargetObjectType,
} from '@fullstory/proto/fs/services/search-settings';
import { int64 } from '@fullstory/proto/google/protobuf';
import { claimExhaustive } from '@fullstory/typeutils';

/**
 * A spec definition and its configuration for the current org.
 *
 * Generally, you should only present active specs to the user; the only
 * exception to this rule is the spec settings page, where the user can
 * configure inactive specs.
 */
export interface SpecInfo {
  /** The spec's unique identifier. */
  slug: string;

  /**
   * True if this spec is active. We only promote interactions with active
   * specs; for example, if a spec is inactive, we won't suggest that the
   * customer assign roles from that spec. Inactive specs which have no mappings
   * and are not used in any library objects are totally invisible to the
   * customer.
   */
  active: boolean;

  /**
   * True if this spec should be available for the current org to configure in
   * settings. A spec may not be configurable if it's controlled by a feature
   * flag which is not enabled for the current org.
   */
  configurable: boolean;

  /** The name that should be displayed for this spec in the UI. */
  displayName: string;
}

/**
 * A spec object and its configuration for the current org.
 *
 * Generally, you're presenting a spec object to the user in one of a few
 * contexts. The best practices are:
 * 1. When the user is selecting a spec object to *use*, e.g. in search, present
 *    only active spec objects with mappings. However, if the user is editing a
 *    saved object that already references an inactive or unmapped spec object,
 *    you should still present it.
 * 2. When the user is selecting a spec object to *map something to*, e.g. in
 *    data object definition modals, present only active spec objects. However,
 *    if the user is editing a saved object that already references an inactive
 *    spec object, you should still present it.
 * 3. When the user is configuring spec objects in the spec settings page,
 *    present only configurable objects.
 */
export type SpecObjectInfo = {
  /** The spec object's unique identifier. */
  slug: string;

  /** The name that should be displayed for this spec object in the UI. */
  displayName: string;

  /** The spec associated with this spec object. */
  spec: SpecInfo;

  /** This spec object's configuration. */
  config: SpecObjectConfigInfo;

  /** The types of objects that this spec object can be mapped to. */
  mappableTypes: SpecTargetObjectType[];

  /** The properties that this spec object has. */
  properties: SpecObjectProperty[];

  /** The mapping for this spec object, if any. */
  mapping?: SpecObjectMappingInfo;

  /**
   * True if this spec object should be available to the user to use (e.g. in
   * search) or to map something to (e.g. in data object definition modals).
   * This includes any spec object in an active spec which is not skipped.
   * However, deprecated spec objects can only be active if they have a mapping;
   * otherwise, they're hidden from the user.
   *
   * It's important to remember that if the user is editing a saved object that
   * references an inactive or unmapped spec object, you should still present it!
   */
  active: boolean;

  /**
   * True if this spec object should be available to the user in the spec
   * settings page. This includes any spec object in a configurable spec, even
   * if it's skipped. However, deprecated spec objects are only configurable if
   * they have a mapping; otherwise, they're hidden from the user.
   */
  configurable: boolean;
};

/** Customer-defined configuration for a spec object. */
export type SpecObjectConfigInfo = {
  /** If true, the customer deliberately skipped mapping this object. */
  skipped: boolean;
  /** Arbitrary notes. */
  notes: string;
};

export type BySlug<T> = { [objectSlug: string]: T };

/**
 * The kinds of objects that can be the target of a spec object mapping.
 * This is a more convenient version of SpecTargetObjectType; the two are
 * interchangeable.
 */
export const TargetType = {
  Invalid: SpecTargetObjectType.SPEC_TARGET_OBJECT_TYPE_INVALID,
  ApiEvent: SpecTargetObjectType.SPEC_TARGET_OBJECT_TYPE_API_EVENT,
  DefinedEvent: SpecTargetObjectType.SPEC_TARGET_OBJECT_TYPE_DEFINED_EVENT,
  Element: SpecTargetObjectType.SPEC_TARGET_OBJECT_TYPE_ELEMENT,
  Page: SpecTargetObjectType.SPEC_TARGET_OBJECT_TYPE_PAGE,
} as const;
export type TargetType = (typeof TargetType)[keyof typeof TargetType];

export type SpecObjectMappingInfoShared = {
  /** The spec object which is being mapped. */
  objectSlug: string;
  /** The type of data object which is the target of the mapping. */
  targetObjectType: TargetType;
  /** The id of the data object which is the target of the mapping. */
  targetObjectId: string;
  /** Mappings for the properties of this spec object. */
  properties: SpecPropertyMapping[];
  /** This mapping's audit metadata, in auditstore format. */
  metadata: AuditMetadata;
  /** The indicator if the mapping was suggested and automapped by Fullstory */
  autosuggested: boolean;
};

/** A spec object mapping to an API event. */
export type SpecApiEventMappingInfo = {
  targetObjectType: typeof TargetType.ApiEvent;
  /** The API event's unique identifier. */
  apiEventId: string;
  definedEventId?: undefined;
  elementId?: undefined;
  pageId?: undefined;
} & SpecObjectMappingInfoShared;

/** A spec object mapping to a defined event. */
export type SpecDefinedEventMappingInfo = {
  targetObjectType: typeof TargetType.DefinedEvent;
  apiEventId?: undefined;
  /** The defined event's unique identifier. */
  definedEventId: string;
  elementId?: undefined;
  pageId?: undefined;
} & SpecObjectMappingInfoShared;

/** A spec object mapping to an element. */
export type SpecElementMappingInfo = {
  targetObjectType: typeof TargetType.Element;
  apiEventId?: undefined;
  definedEventId?: undefined;
  /** The element's unique identifier. */
  elementId: string;
  pageId?: undefined;
} & SpecObjectMappingInfoShared;

/** A spec object mapping to a page. */
export type SpecPageMappingInfo = {
  targetObjectType: typeof TargetType.Page;
  apiEventId?: undefined;
  definedEventId?: undefined;
  elementId?: undefined;
  /** The page's unique identifier. */
  pageId: number;
} & SpecObjectMappingInfoShared;

/** A spec object mapping. */
export type SpecObjectMappingInfo =
  | SpecElementMappingInfo
  | SpecApiEventMappingInfo
  | SpecDefinedEventMappingInfo
  | SpecPageMappingInfo;

export type KnownSpecAttributes = {
  ActiveByDefault?: string;
  ActiveByDefaultForIndustries?: string;
};

const specEntityDisplayName = (entity: Spec | SpecObject | SpecObjectProperty): string =>
  entity.metadata?.displayName || entity.metadata?.name || entity.slug;

const specIsConfigurable = (
  spec: Spec,
  mappings: SpecObjectMapping[],
  flags: FeatureFlags,
): boolean => {
  // Customers always have access to specs that they have mappings for;
  // otherwise, things could get weird, since they'd have mappings that they
  // wouldn't be able to manage.
  const specPrefix = `${spec.slug}/`;
  if (mappings.some(mapping => mapping.objectSlug.startsWith(specPrefix))) {
    return true;
  }

  switch (spec.slug) {
    case 'E-commerce':
      return true;
    case 'DX':
      return flags.AppSpecDx;
    default:
      return false;
  }
};

const specIsActive = (
  spec: Spec,
  config: SpecConfig | undefined,
  configurable: boolean,
  orgIndustry: string,
): boolean => {
  // If the customer is not allowed to configure a spec, it's also never active.
  if (!configurable) {
    return false;
  }

  // If a spec has been manually enabled or disabled by the customer, their
  // decision takes precedence over the defaults.
  if (config) {
    return config.active;
  }

  const specAttributes: KnownSpecAttributes | undefined = spec.metadata?.attributes;

  // The metadata attribute `ActiveByDefault` makes a spec active by default for
  // all orgs.
  const specDefault = specAttributes?.ActiveByDefault;
  if (specDefault === 'true') {
    return true;
  }

  // The metadata attribute `ActiveByDefaultForIndustries` can be set to a
  // comma-delimited list of industries. If one of the industries matches the
  // org's industry, the spec is active by default for that org.
  const specIndustries = specAttributes?.ActiveByDefaultForIndustries;
  if (typeof specIndustries === 'string' && specIndustries !== '') {
    // We normalize the entries to lowercase to match the normalization we
    // perform for the org's industry in getOrgIndustry().
    const specIndustryList = specIndustries.split(',').map(s => s.trim().toLowerCase());
    if (specIndustryList.includes(orgIndustry)) {
      return true;
    }
  }

  // For now, the 'E-commerce' spec is active by default for every org.
  // TODO(sethf): Remove this once the E-commerce spec is configured
  // appropriately. We'll also probably want the DX spec to ship first so
  // customers will always have at least one spec that's active by default,
  // though that's not a requirement.
  if (spec.slug === 'E-commerce') {
    return true;
  }

  // Specs are otherwise inactive by default.
  return false;
};

export const toSpecInfo = (
  spec: Spec,
  config: SpecConfig | undefined,
  mappings: SpecObjectMapping[],
  orgIndustry: string,
  flags: FeatureFlags,
): SpecInfo => {
  const configurable = specIsConfigurable(spec, mappings, flags);
  return {
    slug: spec.slug,
    active: specIsActive(spec, config, configurable, orgIndustry),
    configurable,
    displayName: specEntityDisplayName(spec),
  };
};

export const toSpecObjectConfigInfo = (
  config: SpecObjectConfig | undefined,
): SpecObjectConfigInfo => ({
  skipped: config?.skipped ?? false,
  notes: config?.notes ?? '',
});

const toAuditMetadata = (record: SpecObjectMappingRecord): AuditMetadata => {
  const version = record.metadata?.version ?? int64.fromNumber(0);
  return {
    archived: false,
    visibility: record.metadata?.visibility ?? Visibility.UNSET,
    createdBy: record.metadata?.createdBy ?? '',
    created: record.metadata?.created ?? null,
    lastUpdatedBy: record.metadata?.lastUpdatedBy ?? '',
    lastUpdated: record.metadata?.lastUpdated ?? null,
    sequentialVersion: version,
    version,
  };
};

const toSpecObjectMappingRecordMetadata = (
  record: SpecObjectMappingRecord,
): SpecObjectMappingRecordMetadata => ({
  autosuggested: record.specObjectMappingRecordMetadata?.autosuggested ?? false,
});

export const toSpecObjectMappingInfo = (
  record: SpecObjectMappingRecord,
): SpecObjectMappingInfo | undefined => {
  const sharedFields = {
    objectSlug: record.mapping!.objectSlug,
    targetObjectId: record.mapping!.targetObjectId,
    properties: record.mapping!.properties,
    metadata: toAuditMetadata(record),
    autosuggested: toSpecObjectMappingRecordMetadata(record).autosuggested,
  };
  switch (record.mapping!.targetObjectType) {
    case SpecTargetObjectType.SPEC_TARGET_OBJECT_TYPE_INVALID:
      return undefined;

    case SpecTargetObjectType.SPEC_TARGET_OBJECT_TYPE_API_EVENT:
      return {
        ...sharedFields,
        targetObjectType: TargetType.ApiEvent,
        apiEventId: record.mapping!.targetObjectId,
      };

    case SpecTargetObjectType.SPEC_TARGET_OBJECT_TYPE_DEFINED_EVENT:
      return {
        ...sharedFields,
        targetObjectType: TargetType.DefinedEvent,
        definedEventId: record.mapping!.targetObjectId,
      };

    case SpecTargetObjectType.SPEC_TARGET_OBJECT_TYPE_ELEMENT:
      return {
        ...sharedFields,
        targetObjectType: TargetType.Element,
        elementId: record.mapping!.targetObjectId,
      };

    case SpecTargetObjectType.SPEC_TARGET_OBJECT_TYPE_PAGE:
      return {
        ...sharedFields,
        targetObjectType: TargetType.Page,
        pageId: Number(record.mapping!.targetObjectId),
      };

    default:
      claimExhaustive(record.mapping!.targetObjectType);
      return undefined;
  }
};

const DX_ACCOUNT_OBJECT_SLUGS = new Set([
  // Pages
  'DX/Account Creation Page',
  'DX/Account Recovery Page',
  // Elements
  'DX/Sign Out Button',
  'DX/Submit One Time Passcode',
  'DX/Unlock Account Button',
  'DX/Username Input',
  'DX/Create Account Button',
  'DX/Forgot Password Button',
  'DX/Change Password Button',
  'DX/Forgot Username Button',
  'DX/Remember Device',
]);

export const isHiddenDueToFlags = (object: SpecObject, flags: FeatureFlags): boolean => {
  if (!flags.AppSpecDxAccount && DX_ACCOUNT_OBJECT_SLUGS.has(object.slug)) {
    return true;
  }
  return false;
};

export const toSpecObjectInfo = (
  spec: SpecInfo,
  object: SpecObject,
  config: SpecObjectConfig | undefined,
  mapping: SpecObjectMappingInfo | undefined,
  flags: FeatureFlags,
): SpecObjectInfo => {
  const displayName = specEntityDisplayName(object);
  const hiddenDueToDeprecation = object.metadata?.deprecated && !mapping;
  const hiddenDueToFlags = isHiddenDueToFlags(object, flags);
  const configurable = spec.configurable && !hiddenDueToDeprecation && !hiddenDueToFlags;
  const active = configurable && spec.active && !config?.skipped;
  return {
    slug: object.slug,
    displayName,
    spec,
    config: toSpecObjectConfigInfo(config),
    mappableTypes: object.targetObjectTypes,
    properties: object.properties,
    mapping,
    active,
    configurable,
  };
};
